import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, PenLine } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { DraftyMascot } from './DraftyMascot';

/**
 * First-login role selection — shown once, when a signed-in Google user has
 * no profile yet and their entry path didn't reveal a role. One screen, two
 * big cards, no forms. Returning users never see this.
 */
const RoleSelect = () => {
  const { user, selectRole, signOut } = useAuth();
  const [choosing, setChoosing] = useState(null); // 'teacher' | 'student' | null

  const choose = async (role) => {
    if (choosing) return;
    setChoosing(role);
    try {
      await selectRole(role);
    } finally {
      setChoosing(null);
    }
  };

  const firstName = (user?.user_metadata?.full_name || user?.email || '').split(/[\s@]/)[0];

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-3xl w-full text-center"
      >
        <DraftyMascot size="lg" mood="excited" className="mx-auto mb-6" />
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
          Welcome to DraftFlow{firstName ? `, ${firstName}` : ''}!
        </h1>
        <p className="text-slate-400 mt-3 mb-10">
          One quick question so we can set up the right workspace for you.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <button
            data-testid="role-select-teacher"
            onClick={() => choose('teacher')}
            disabled={!!choosing}
            className="card-surface p-8 text-left hover:border-flow-blue/50 focus:outline-none focus:ring-2 focus:ring-flow-blue transition-colors disabled:opacity-60 group"
          >
            <div className="w-14 h-14 rounded-xl bg-flow-blue/15 text-flow-blue-hover flex items-center justify-center mb-5 group-hover:bg-flow-blue/25 transition-colors">
              <GraduationCap size={30} aria-hidden="true" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-white mb-2">
              I'm a teacher
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              Create assignments, build rubrics, and see exactly how every
              student's writing is growing.
            </p>
            <span className="inline-block mt-5 text-flow-blue-hover font-medium text-sm">
              {choosing === 'teacher' ? 'Setting up your classroom…' : 'Set up my classroom →'}
            </span>
          </button>

          <button
            data-testid="role-select-student"
            onClick={() => choose('student')}
            disabled={!!choosing}
            className="card-surface p-8 text-left hover:border-ai-accent/50 focus:outline-none focus:ring-2 focus:ring-ai-accent transition-colors disabled:opacity-60 group"
          >
            <div className="w-14 h-14 rounded-xl bg-ai-accent/15 text-ai-accent flex items-center justify-center mb-5 group-hover:bg-ai-accent/25 transition-colors">
              <PenLine size={30} aria-hidden="true" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-white mb-2">
              I'm a student
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              Write with a coach that asks the right questions — and never
              writes a word for you.
            </p>
            <span className="inline-block mt-5 text-ai-accent font-medium text-sm">
              {choosing === 'student' ? 'Getting your desk ready…' : 'Start writing →'}
            </span>
          </button>
        </div>

        <p className="text-slate-500 text-sm mt-10">
          Signed in as {user?.email}.{' '}
          <button
            onClick={signOut}
            className="text-slate-400 hover:text-white underline"
            data-testid="role-select-signout"
          >
            Not you? Sign out
          </button>
        </p>
      </motion.div>
    </div>
  );
};

export default RoleSelect;
